import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { ChartFrame, HBarChart, LineChart, StatTile, rupeesShort } from './charts';

/** Mirrors Eduvora.Application.Overviews. */
export interface ModuleTile {
  label: string;
  value: string;
  foot: string | null;
  tone: string | null; // "ok" | "warn" | "danger" | null
}

export interface ModulePoint {
  label: string;
  value: number;
}

export interface ModuleOverviewDto {
  module: string;
  headline: string;
  tiles: ModuleTile[];
  chartTitle: string | null;
  chartSubtitle: string | null;
  /** "money" | "percent" | "count" — how to format the chart's values. */
  valueKind: string;
  chart: ModulePoint[];
  note: string | null;
}

export type ModuleName = 'fees' | 'attendance' | 'hr' | 'transport';

/**
 * THE OVERVIEW STRIP that sits at the top of a module page.
 *
 * ⚠ ONE COMPONENT, NOT ONE PER MODULE. The prospect's complaint was that with 27 modules, none of
 * them opened on anything — every page dropped him straight into a table. Twenty-seven bespoke
 * dashboards is months of work and 27 things to maintain. One generic contract that each module
 * fills in is days, and adding the 28th is a `case` on the server plus one line here.
 *
 * ⚠ IT FAILS SILENTLY, ON PURPOSE. If the strip 403s or errors it renders NOTHING and the page below
 * carries on exactly as before. A strip is an enhancement to a page that already worked; a broken
 * enhancement must never take a working page down with it — and roles that cannot see the strip
 * (a Receptionist on the Fees page) simply do not get one, rather than an error they cannot act on.
 */
export function ModuleStrip({ module }: { module: ModuleName }) {
  const [d, setD] = useState<ModuleOverviewDto | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .get<ModuleOverviewDto>(`/api/overviews/${module}`)
      .then((r) => alive && setD(r))
      .catch(() => {
        /* silent by design — see the note above */
      });
    return () => {
      alive = false;
    };
  }, [module]);

  // ⚠ EVERY ARRAY IS CHECKED, NOT JUST THE OBJECT — and the difference took down a whole page.
  // This component's own note above says it fails silently on purpose. It did not: `!d` catches a
  // 403 or a network error, but a 200 carrying a PARTIALLY shaped strip — an older API build, a
  // module the server does not know about yet, a field renamed — leaves `d` truthy and `d.tiles`
  // undefined, and `.length` on undefined throws during render. React then unmounts the entire
  // tree, so a decorative strip at the top of the page takes the HR screen, the fee screen, the
  // attendance screen and the transport screen down with it. Found by previewing a page whose
  // fixture had no strip in it.
  const tiles = d?.tiles ?? [];
  const chart = d?.chart ?? [];
  if (!d || tiles.length === 0) return null;

  const fmt = (n: number) =>
    d.valueKind === 'money' ? rupeesShort(n)
      : d.valueKind === 'percent' ? `${n.toFixed(1)}%`
        : String(Math.round(n));

  // A time series wants a line; a set of named categories wants bars.
  const isSeries = d.valueKind === 'money' && chart.length > 6;

  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))',
          gap: 12,
          marginBottom: chart.length > 0 ? 12 : 0,
        }}
      >
        {tiles.map((t) => (
          <div className="card" key={t.label} style={{ padding: 16 }}>
            <StatTile
              label={t.label}
              value={t.value}
              foot={t.foot ? <span style={{ color: tone(t.tone) }}>{t.foot}</span> : undefined}
            />
          </div>
        ))}
      </div>

      {chart.length > 0 && d.chartTitle && (
        <div className="card" style={{ padding: 16 }}>
          <ChartFrame title={d.chartTitle} subtitle={d.chartSubtitle ?? undefined}>
            {isSeries ? (
              <LineChart
                labels={chart.map((p) => p.label)}
                series={[{ name: d.chartTitle, color: 'var(--s1)', values: chart.map((p) => p.value) }]}
                format={fmt}
                height={150}
              />
            ) : (
              <HBarChart
                categories={chart.map((p) => p.label)}
                series={[{ name: d.chartTitle, color: 'var(--s1)', values: chart.map((p) => p.value) }]}
                format={fmt}
                labelWidth={116}
                valueWidth={d.valueKind === 'money' ? 80 : 68}
                colorAt={d.valueKind === 'percent' ? (i) => band(chart[i].value) : undefined}
              />
            )}
          </ChartFrame>
          {d.note && (
            <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 10, marginBottom: 0, lineHeight: 1.55 }}>
              ⚠ {d.note}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function tone(t: string | null) {
  return t === 'ok' ? 'var(--ok)' : t === 'warn' ? 'var(--warn)' : t === 'danger' ? 'var(--danger)' : 'var(--ink-2)';
}

/* ⚠ Status colour, legal only because every bar is directly labelled with its own percentage. */
function band(v: number) {
  return v >= 90 ? 'var(--ok)' : v >= 75 ? 'var(--warn)' : 'var(--danger)';
}
